import { createTheme } from '@mui/material/styles';

// Dark theme color palette
const darkColors = {
  background: {
    default: '#1e1e1e', // Lighter dark grey (Discord/Slack style)
    paper: '#2b2b2b', // Medium dark grey for cards
    subtle: '#242424', // Subtle darker grey
  },
  border: {
    default: '#3a3a3a',
    hover: '#4a4a4a',
  },
  text: {
    primary: '#ededed',
    secondary: '#a1a1a1',
    muted: '#888888',
  },
  primary: {
    main: '#0a84ff', // iOS blue for dark mode
    light: '#64d2ff',
    dark: '#005ecc',
    contrastText: '#ffffff',
  },
  error: {
    main: '#ff453a', // iOS red for dark mode (profit negative)
    light: '#ff6961',
    dark: '#d70015',
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
  },
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
  },
  success: {
    main: '#30d158', // iOS green for dark mode (profit positive)
    light: '#64d88d',
    dark: '#248a3d',
  },
};

// Light theme color palette - iOS-inspired with subtle depth
const lightColors = {
  background: {
    default: '#e8e8e8', // Darker grey background
    paper: '#f0f0f0', // Lighter grey for cards/paper
    subtle: '#d5d5d5', // Subtle darker grey
  },
  border: {
    default: 'rgba(0, 0, 0, 0.08)', // Softer, transparent borders (was #e1e5e9)
    hover: 'rgba(0, 0, 0, 0.12)',
  },
  text: {
    primary: '#1a1a1a',
    secondary: '#6c757d',
    muted: '#9ca3af',
  },
  primary: {
    main: '#007aff', // iOS blue for light mode
    light: '#5ac8fa',
    dark: '#0051d5',
    contrastText: '#ffffff',
  },
  error: {
    main: '#ff3b30', // iOS red for light mode (profit negative)
    light: '#ff6961',
    dark: '#c9302c',
  },
  warning: {
    main: '#ffc107',
    light: '#ffca2c',
    dark: '#ff8f00',
  },
  info: {
    main: '#0dcaf0',
    light: '#3dd5f3',
    dark: '#087990',
  },
  success: {
    main: '#34c759', // iOS green for light mode (profit positive)
    light: '#5dd879',
    dark: '#248a3d',
  },
};

// Create theme function that accepts colors and mode
const createDealerOSTheme = (colors: any, mode: 'light' | 'dark') => createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode,
    background: colors.background,
    primary: colors.primary,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    success: colors.success,
    text: colors.text,
    divider: colors.border.default,
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
    fontSize: 17, // iOS standard base font size
    h1: {
      fontSize: 'clamp(2rem, 4vw, 2.75rem)',
      fontWeight: 700,
      letterSpacing: '-0.025em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: 'clamp(1.625rem, 3vw, 2.25rem)',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: 'clamp(1.375rem, 2.5vw, 1.75rem)',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h4: {
      fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: 'clamp(0.9375rem, 1.25vw, 1.125rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1.0625rem', // 17px - iOS standard
      lineHeight: 1.47, // iOS standard line-height
      letterSpacing: '-0.022em', // SF Pro tracking
      fontWeight: 400,
    },
    body2: {
      fontSize: '1rem', // 16px
      lineHeight: 1.5,
      letterSpacing: '-0.02em', // SF Pro tracking
      fontWeight: 400,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '1.0625rem', // 17px - iOS standard
      lineHeight: 1.47, // iOS standard
      letterSpacing: '-0.022em', // SF Pro tracking
    },
  },
  shape: {
    borderRadius: 12, // iOS-style rounding (was 6px)
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          color: colors.text.primary,
          fontFeatureSettings: '"cv11", "ss01", "ss03"',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: colors.background.default,
        },
        '::-webkit-scrollbar-thumb': {
          background: colors.border.default,
          borderRadius: '4px',
          '&:hover': {
            background: colors.border.hover,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true, // NO RIPPLE ANIMATION - Instant click feedback
      },
      styleOverrides: {
        root: {
          borderRadius: '12px', // iOS-style rounding
          minHeight: '44px', // Minimum touch target size
          fontSize: '1rem',
          fontWeight: 500,
          transition: 'all 0.05s cubic-bezier(0.4, 0, 1, 1)', // INSTANT (was 0.15s)
          boxShadow: 'none',
          outline: 'none', // NO OUTLINE
          '&:focus': {
            outline: 'none', // NO FOCUS OUTLINE
          },
          '&:focus-visible': {
            outline: 'none', // NO FOCUS OUTLINE even for keyboard nav
          },
          '@media (max-width: 600px)': {
            minHeight: '48px', // Larger touch targets on mobile
            fontSize: '1.0625rem',
            padding: '12px 20px',
          },
          // Hover (desktop only)
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: mode === 'light'
                ? '0 2px 4px rgba(0, 0, 0, 0.08)' // Subtle lift shadow in light mode
                : 'none',
            },
          },
          // Active/Press (INSTANT feedback)
          '&:active': {
            transform: 'scale(0.96)', // Press feedback
            transition: 'all 0.05s cubic-bezier(0.4, 0, 1, 1)', // INSTANT (was 0.1s)
          },
        },
        sizeSmall: {
          minHeight: '36px',
          fontSize: '0.9375rem',
          padding: '4px 10px',
          '@media (max-width: 600px)': {
            minHeight: '44px',
            fontSize: '1rem',
            padding: '8px 16px',
          },
        },
        sizeMedium: {
          padding: '8px 22px',
          '@media (max-width: 600px)': {
            padding: '12px 24px',
          },
        },
        sizeLarge: {
          minHeight: '48px',
          padding: '12px 28px',
          fontSize: '1.0625rem',
          '@media (max-width: 600px)': {
            minHeight: '56px',
            padding: '16px 32px',
            fontSize: '1rem',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          '&:hover': {
            backgroundColor: colors.primary.light,
          },
        },
        outlined: {
          borderColor: colors.border.default,
          color: colors.text.primary,
          '&:hover': {
            borderColor: colors.border.hover,
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          },
        },
        text: {
          color: colors.text.secondary,
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            color: colors.text.primary,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.default}`, // Subtle border (was 2px)
          borderRadius: '12px', // iOS-style rounding
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)' // iOS-style subtle shadow
            : 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.default}`, // Subtle border (was 2px - too heavy in light mode)
          borderRadius: '16px', // iOS-style rounding for cards
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)' // iOS-style subtle shadow
            : 'none',
          transition: 'all 0.1s cubic-bezier(0.4, 0, 1, 1)', // INSTANT (was 0.2s)
          '&:hover': {
            borderColor: colors.border.hover,
            transform: 'translateY(-2px)', // Subtle lift
            boxShadow: mode === 'light'
              ? '0 4px 6px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)' // Lifted shadow
              : 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.background.default,
            fontSize: '1rem',
            '& fieldset': {
              borderColor: colors.border.default,
            },
            '&:hover fieldset': {
              borderColor: colors.border.hover,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: '1px',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            color: colors.text.secondary,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.subtle,
          '& .MuiTableCell-head': {
            color: colors.text.secondary,
            fontWeight: 500,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: `1px solid ${colors.border.default}`,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
            },
          },
          '& .MuiTableCell-body': {
            borderBottom: `1px solid ${colors.border.default}`,
            fontSize: '1rem',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px', // iOS-style (was 4px)
          fontSize: '0.875rem',
          height: '24px',
          fontWeight: 500, // Slightly bolder for iOS
          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
          border: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.default}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border.default,
        },
      },
    },
    MuiListItemButton: {
      defaultProps: {
        disableRipple: true, // NO RIPPLE ANIMATION - Instant click feedback
      },
      styleOverrides: {
        root: {
          borderRadius: '8px', // iOS-style (was 6px)
          marginBottom: '2px',
          fontSize: '1rem',
          transition: 'none', // NO ANIMATION - Instant state change
          border: 'none', // NO BORDER
          outline: 'none', // NO OUTLINE
          '&:focus': {
            outline: 'none', // NO FOCUS OUTLINE
          },
          '&:focus-visible': {
            outline: 'none', // NO FOCUS OUTLINE even for keyboard nav
          },
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: mode === 'dark' ? 'rgba(16, 185, 129, 0.1)' : `rgba(${parseInt(colors.primary.main.slice(1, 3), 16)}, ${parseInt(colors.primary.main.slice(3, 5), 16)}, ${parseInt(colors.primary.main.slice(5, 7), 16)}, 0.1)`,
            color: colors.primary.main,
            border: 'none', // NO BORDER on selected
            outline: 'none', // NO OUTLINE on selected
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(16, 185, 129, 0.15)' : `rgba(${parseInt(colors.primary.main.slice(1, 3), 16)}, ${parseInt(colors.primary.main.slice(3, 5), 16)}, ${parseInt(colors.primary.main.slice(5, 7), 16)}, 0.15)`,
            },
            '&:focus': {
              outline: 'none',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontSize: '1rem',
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 0, // NO DELAY - Instant tooltip appearance
        leaveDelay: 0, // NO DELAY - Instant tooltip disappearance
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.background.subtle,
          border: `1px solid ${colors.border.default}`,
          color: colors.text.primary,
          fontSize: '0.875rem',
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true, // NO RIPPLE ANIMATION - Instant click feedback
      },
      styleOverrides: {
        root: {
          minWidth: '44px',
          minHeight: '44px',
          transition: 'none', // NO ANIMATION - Instant state change
          outline: 'none', // NO OUTLINE
          '&:focus': {
            outline: 'none', // NO FOCUS OUTLINE
          },
          '&:focus-visible': {
            outline: 'none', // NO FOCUS OUTLINE even for keyboard nav
          },
          '@media (max-width: 600px)': {
            minWidth: '48px',
            minHeight: '48px',
          },
        },
        sizeSmall: {
          minWidth: '36px',
          minHeight: '36px',
          '@media (max-width: 600px)': {
            minWidth: '44px',
            minHeight: '44px',
          },
        },
        sizeLarge: {
          minWidth: '56px',
          minHeight: '56px',
          '@media (max-width: 600px)': {
            minWidth: '60px',
            minHeight: '60px',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            width: '64px',
            height: '64px',
          },
        },
        sizeSmall: {
          '@media (max-width: 600px)': {
            width: '48px',
            height: '48px',
          },
        },
      },
    },
  },
});

// Export theme instances
export const supabaseTheme = createDealerOSTheme(darkColors, 'dark');
export const supabaseThemeLight = createDealerOSTheme(lightColors, 'light');

// Export default as dark theme for backward compatibility
export default supabaseTheme;